import { Col, Divider, Dropdown, MenuProps, notification, Row, Space, Tooltip } from "antd"
import { Proposal, calculateTotalPrice, ProposalJobData } from "./proposalsModel"
import { faEnvelope, faEye, faFile, faFileInvoiceDollar, faLink } from "@fortawesome/pro-solid-svg-icons"
import { useCreateAttachmentUrlMutation, useCreateExistingProposalLinkMutation, useCreatePreviewLinkMutation } from "./proposalsQueries"
import { centsToDollarsDisplay } from "../../displayFormatters"
import ProposalRecipients from "./ProposalRecipients"
import ProposalWorkareas from "./ProposalWorkareas"
import ProposalTermsAndConditions from "./ProposalTermsAndConditions"
import ProposalPaymentSchedule from "./ProposalPaymentSchedule"
import IconAndTextButton from "../../IconAndTextButton"
import ProposalTimeline from "./ProposalTimeline"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import SendProposalModal from "../../Proposals/SendProposalModal"
import { useState } from "react"
import LinkCreatedModal from "../../Proposals/LinkedCreatedModal/LinkCreatedModal"

type Props = {
  latestProposal: Proposal
  onCreateNewProposal: () => void
  onInvalidateJob: () => void
  job: ProposalJobData
}

const LatestProposal = ({ latestProposal, onCreateNewProposal, onInvalidateJob, job }: Props) => {
  const [isResendModalOpen, setIsResendModalOpen] = useState(false)
  const [proposalLink, setProposalLink] = useState<string | undefined>();
  const customerIsActive = job?.customer?.isActive ?? false

  const createExistingProposalLink = useCreateExistingProposalLinkMutation((link: string) => {
    setProposalLink(link)
  })
  const createPreviewLinkMutation = useCreatePreviewLinkMutation((link: string) => {
    setTimeout(() => window.open(link, '_blank'), 0)
  })
  const createAttachmentUrl = useCreateAttachmentUrlMutation((url: string) => {
    if (!url) {
      notification.warning({ message: 'The proposal PDF could not be found. It may have been deleted.' })
      return
    }
    setTimeout(() => window.open(url, '_blank'), 0)
  })

  const workareas = latestProposal.workareas ?? []

  const items: MenuProps['items'] = [
    {
      label: 'View PDF',
      key: '1',
      disabled: !latestProposal.initialAttachmentId,
      icon: <FontAwesomeIcon icon={faFile} />
    },
    {
      label: 'View Link',
      key: '2',
      icon: <FontAwesomeIcon icon={faLink} />
    },
  ];

  const handleMenuClick: MenuProps['onClick'] = (e) => {
    switch (e.key) {
      case '1': {
        if (latestProposal.initialAttachmentId) {
          createAttachmentUrl.mutate(latestProposal.initialAttachmentId)
        }
        break;
      }
      case '2': {
        createExistingProposalLink.mutate(latestProposal.id)
        break;
      }
      default: {
        break;
      }
    }
  };

  const menuProps = {
    items,
    onClick: handleMenuClick,
  };

  const totalPrice = calculateTotalPrice(workareas)

  return <div>
    <div>
      <span style={{ display: 'flex' }}>
        <div style={{ fontSize: '18px', width: '50%' }}>Current Proposal</div>
        <div style={{ width: '45%', fontSize: '14px', marginBottom: '5px' }}>
          <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span>Total Amount: <b>{centsToDollarsDisplay(totalPrice)}</b></span>
          </div>
        </div>
      </span>
      <Divider></Divider>
      <Row>
        <Col span={4}>
          <ProposalTimeline latestProposal={latestProposal} />
        </Col>
        <Col span={1}></Col>
        <Col span={18}>
          <Row style={{ flexDirection: 'column', alignItems: 'flex-end', marginBottom: '10px' }}>
            <Col>
              <Space>
                <Tooltip title={customerIsActive ? 'Create New Proposal': 'Proposal creation is disabled due to the customer being inactive.'}>
                  <Space>
                    <IconAndTextButton
                      type="primary"
                      size="large"
                      style={{ marginLeft: '5px' }}
                      onClick={onCreateNewProposal}
                      icon={faFileInvoiceDollar}
                      text="Create New Proposal"
                      disabled={!customerIsActive}
                    />
                  </Space>
                </Tooltip>
                <Dropdown.Button type="default" menu={menuProps} size="large" onClick={() => setIsResendModalOpen(true)}>
                  <FontAwesomeIcon icon={faEnvelope} />
                  <span style={{ fontSize: '14px', marginLeft: '5px' }}> Send</span>
                </Dropdown.Button>

                <IconAndTextButton icon={faEye} text="Preview" size="large" onClick={() => createPreviewLinkMutation.mutate(latestProposal.id)} />
              </Space>
            </Col>
          </Row>
          <ProposalRecipients latestProposal={latestProposal} onInvalidateJob={onInvalidateJob} />
          <ProposalWorkareas workareas={latestProposal.workareas ?? []} jobId={job.id} />
          <ProposalTermsAndConditions termsAndConditions={latestProposal.termsAndConditions} />
          <ProposalPaymentSchedule
            latestProposalId={latestProposal.id}
            workareas={workareas}
          />
        </Col>
      </Row>
    </div>

    <SendProposalModal
      isOpen={isResendModalOpen}
      job={job}
      proposalId={latestProposal.id}
      proposalTotal={totalPrice}
      onClose={() => setIsResendModalOpen(false)}
    />

    <LinkCreatedModal
      visible={proposalLink !== undefined}
      linkIsFromExistingProposal={true}
      link={proposalLink ?? ''}
      onClose={() => setProposalLink(undefined)}
    />
  </div>
}

export default LatestProposal
